/**
 * DA-436: Utility for persisting provider service data through authentication flow.
 * Used when unauthenticated users fill in service details on homepage before registering.
 *
 * Flow:
 * 1. Anonymous user fills service form on homepage (meindienstarzt.de)
 * 2. On submit: encode data in URL, redirect to app sign-up (app.meindienstarzt.de)
 * 3. Sign-up page decodes URL param and stores in localStorage
 * 4. After registration: restore data and create service
 *
 * DA-463: Added cross-domain support via URL query params
 * Since localStorage is origin-bound, we use base64-encoded URL params to transfer
 * data from marketing site (meindienstarzt.de) to app (app.meindienstarzt.de)
 */

import { type ServiceType, type CompensationType } from '@/types/validators';
import { buildAuthFlowRedirectUrl } from '@/lib/auth-flow-transfer';
import { APP_URL } from '@/lib/app-url';

const STORAGE_KEY = 'pendingProviderService';

export type PendingServiceData = {
  sourceKey?: string;
  // Service details
  serviceType: ServiceType | '';
  startDate: string; // ISO string or datetime-local format
  endDate: string;
  serviceAreaId?: string;
  serviceAreaName?: string; // For display purposes
  kvRegion?: string;

  // DA-476: Manual entry when service area not found in database
  manualServiceArea?: string;
  // DA-476: Manual entry for sub-locations when not found
  manualBereitschaftspraxis?: string;
  manualFahrdienstgebiet?: string;

  // Compensation
  compensationType: CompensationType;
  fixedPrice?: number;
  minPrice?: number;
  maxPrice?: number;

  // Notfallscheinbeteiligung
  hasNotfallscheinbeteiligung: boolean;
  notfallscheinbeteiligungRate?: number;

  // Notes (optional)
  notes?: string;

  // Metadata
  timestamp: number;
};

export const setPendingServiceData = (data: Omit<PendingServiceData, 'timestamp'>): void => {
  const fullData: PendingServiceData = {
    ...data,
    timestamp: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(fullData));
};

export const getPendingServiceData = (): PendingServiceData | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw) as PendingServiceData;

    // Expire after 1 hour
    const ONE_HOUR = 60 * 60 * 1000;
    if (Date.now() - data.timestamp > ONE_HOUR) {
      clearPendingServiceData();
      return null;
    }

    return data;
  } catch {
    clearPendingServiceData();
    return null;
  }
};

export const clearPendingServiceData = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export const hasPendingServiceData = (): boolean => {
  return getPendingServiceData() !== null;
};

// Legacy exports for backward compatibility (used in existing code)
// TODO: Clean up after DA-436 is fully implemented
export type PendingProviderAction = {
  action: 'createService';
  timestamp: number;
};

export const setPendingProviderAction = (action: 'createService'): void => {
  // For legacy code that just sets an action flag
  const data: PendingProviderAction = {
    action,
    timestamp: Date.now(),
  };
  localStorage.setItem('pendingProviderAction_legacy', JSON.stringify(data));
};

export const getPendingProviderAction = (): PendingProviderAction | null => {
  try {
    // Check new format first
    const serviceData = getPendingServiceData();
    if (serviceData) {
      return { action: 'createService', timestamp: serviceData.timestamp };
    }

    // Fall back to legacy format
    const raw = localStorage.getItem('pendingProviderAction_legacy');
    if (!raw) return null;

    const data = JSON.parse(raw) as PendingProviderAction;
    const ONE_HOUR = 60 * 60 * 1000;
    if (Date.now() - data.timestamp > ONE_HOUR) {
      clearPendingProviderAction();
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

export const clearPendingProviderAction = (): void => {
  localStorage.removeItem('pendingProviderAction_legacy');
  // Note: Don't clear service data here, it's handled separately
};

export const hasPendingProviderAction = (): boolean => {
  return getPendingProviderAction() !== null;
};

// =============================================================================
// DA-463: Cross-domain URL encoding/decoding helpers
// =============================================================================

/**
 * Decode service data from URL param (used by app sign-up page)
 * @param encoded Base64 URL-safe encoded string from URL param
 * @returns Decoded service data or null if invalid/expired
 */
export const decodeServiceDataFromUrl = (encoded: string): PendingServiceData | null => {
  try {
    const json = decodeURIComponent(atob(encoded));
    const data = JSON.parse(json) as PendingServiceData;

    // Validate required fields
    if (!data.serviceType || !data.startDate || !data.endDate) {
      console.warn('[decodeServiceDataFromUrl] Missing required fields');
      return null;
    }

    // Check expiration (1 hour)
    const ONE_HOUR = 60 * 60 * 1000;
    if (Date.now() - data.timestamp > ONE_HOUR) {
      console.warn('[decodeServiceDataFromUrl] Data expired');
      return null;
    }

    return data;
  } catch (err) {
    console.error('[decodeServiceDataFromUrl] Failed to decode:', err);
    return null;
  }
};

/**
 * Build the redirect URL for marketing site to use
 * @param data Service data to transfer
 * @param appUrl Base URL of the app (default: https://app.meindienstarzt.de)
 * @returns Full redirect URL with encoded service data
 */
export const buildSignUpRedirectUrl = (transferToken: string, appUrl = APP_URL): string => {
  return buildAuthFlowRedirectUrl({
    appUrl,
    intent: 'createService',
    transferToken,
  });
};

/**
 * Process serviceData URL param on sign-up page
 * Decodes the param and stores in localStorage for later use
 * @returns true if valid service data was found and stored
 */
export const processServiceDataFromUrl = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  const encodedData = searchParams.get('serviceData');

  if (!encodedData) {
    return false;
  }

  const data = decodeServiceDataFromUrl(encodedData);
  if (!data) {
    return false;
  }

  // Store in localStorage (now on app.meindienstarzt.de origin)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  console.log('[processServiceDataFromUrl] Service data stored from URL param');
  return true;
};
